/**
 * PurposeCaseGate — approved purpose code + case/reference capture before any
 * sensitive trace action (detail / search / export) (ODE-P10-T07, REQ-101).
 *
 * The form is intentionally local: nothing is sent until the user confirms,
 * and the T06 backend audits every success/denial (REQ-102). The REQ-110
 * disclaimer is always visible next to the gate so investigation context is
 * never presented as a wrongdoing assertion.
 *
 * Traces to: ODE-PRD-REQ-099, ODE-PRD-REQ-100, ODE-PRD-REQ-101,
 *            ODE-PRD-REQ-110.
 */

import { useState } from 'react';
import { APPROVED_PURPOSE_CODES, CASE_REF_PATTERN, type PurposeCode } from './types.js';

export interface PurposeCaseGateProps {
  readonly actionLabel: string;
  readonly onConfirm: (purposeCode: PurposeCode, caseRef: string) => void;
  readonly onCancel: () => void;
}

export function PurposeCaseGate({ actionLabel, onConfirm, onCancel }: PurposeCaseGateProps): JSX.Element {
  const [purposeCode, setPurposeCode] = useState<PurposeCode>(APPROVED_PURPOSE_CODES[0]);
  const [caseRef, setCaseRef] = useState('');

  const caseRefValid = caseRef.trim() === '' || CASE_REF_PATTERN.test(caseRef.trim());
  const canSubmit = caseRefValid;
  // Live validation: an invalid non-empty case reference is flagged immediately.
  const showCaseRefError = caseRef.trim() !== '' && !caseRefValid;

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSubmit) return;
    onConfirm(purposeCode, caseRef.trim());
  };

  return (
    <section className="purpose-gate" data-testid="purpose-case-gate" aria-label="Kode tujuan dan referensi kasus">
      <h4>{actionLabel}</h4>
      <p className="gate-note">
        Akses sensitif memerlukan kode tujuan yang disetujui dan, saat mode investigasi digunakan,
        referensi kasus — setiap keberhasilan/penolakan dicatat (REQ-101/102).
      </p>
      <form onSubmit={onSubmit} noValidate={false}>
        <label className="purpose-field">
          Kode tujuan
          <select
            data-testid="purpose-code"
            value={purposeCode}
            onChange={(event) => setPurposeCode(event.target.value as PurposeCode)}
          >
            {APPROVED_PURPOSE_CODES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
        <label className="purpose-field">
          Referensi kasus (opsional)
          <input
            data-testid="case-ref"
            value={caseRef}
            onChange={(event) => setCaseRef(event.target.value)}
            placeholder="mis. CASE-2026-0012"
          />
        </label>
        {showCaseRefError && (
          <p role="alert" data-testid="case-ref-error" className="gate-error">
            Referensi kasus harus 1–64 karakter [A-Za-z0-9._:-] atau kosong.
          </p>
        )}
        <div className="gate-actions">
          <button type="submit" data-testid="purpose-confirm" disabled={!canSubmit}>
            Lanjutkan
          </button>
          <button type="button" data-testid="purpose-cancel" onClick={onCancel}>
            Batal
          </button>
        </div>
      </form>
      <p className="gate-disclaimer" data-testid="req110-disclaimer">
        Investigative leads only — tidak ada penentuan kriminalitas otomatis, keputusan disipliner
        final, atau kesimpulan menghukum yang dihasilkan dari frekuensi komunikasi, grafik kontak,
        atau notifikasi teramati (REQ-110).
      </p>
    </section>
  );
}